import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'

// Renders two CSS2D markers in the scene:
//   • cursor crosshair: follows cursorWorld whenever showCoordinates
//     is on, so the user has a 3D anchor for the readout in the HUD.
//   • reference origin: fixed marker at coordsReference, always
//     visible (independent of showCoordinates) so the user knows
//     where their relative-coordinates anchor sits.
//
// Both markers are CSS2DObjects so they billboard automatically and
// keep the same screen size at any zoom. Requires the world's
// renderer to be RendererWith2D.
export function useCoordsMarkers(): void {
  const groupRef = useRef<THREE.Group | null>(null)
  const cursorObjRef = useRef<CSS2DObject | null>(null)
  const refObjRef = useRef<CSS2DObject | null>(null)

  useEffect(() => {
    let teardown: (() => void) | null = null
    const setup = () => {
      teardown?.()
      teardown = null
      const world = viewerApi.getWorld()
      if (!world) return

      const group = new THREE.Group()
      group.name = 'vn2r-coords-markers'
      world.scene.three.add(group)
      groupRef.current = group

      const cursorObj = new CSS2DObject(makeCursorElement())
      cursorObj.visible = false
      group.add(cursorObj)
      cursorObjRef.current = cursorObj

      const refObj = new CSS2DObject(makeReferenceElement())
      refObj.visible = false
      group.add(refObj)
      refObjRef.current = refObj

      // Initial sync from the slice.
      const apply = () => {
        const s = useAppStore.getState()
        const cur = s.cursorWorld
        if (s.showCoordinates && cur) {
          cursorObj.position.set(cur[0], cur[1], cur[2])
          cursorObj.visible = true
        } else {
          cursorObj.visible = false
        }
        const ref = s.coordsReference
        if (ref) {
          refObj.position.set(ref[0], ref[1], ref[2])
          refObj.visible = true
        } else {
          refObj.visible = false
        }
      }
      apply()
      const unsubscribeStore = useAppStore.subscribe((state, prev) => {
        if (
          state.cursorWorld === prev.cursorWorld &&
          state.coordsReference === prev.coordsReference &&
          state.showCoordinates === prev.showCoordinates
        ) {
          return
        }
        apply()
      })

      teardown = () => {
        unsubscribeStore()
        cursorObjRef.current = null
        refObjRef.current = null
        groupRef.current = null
        group.parent?.remove(group)
        // CSS2DObject has its own DOM element to clean up; calling
        // removeFromParent on the object detaches the element from
        // the CSS2DRenderer's tree on the next render.
        for (const child of [...group.children]) {
          group.remove(child)
        }
      }
    }
    setup()
    const unsubscribe = viewerApi.subscribe(setup)
    return () => {
      unsubscribe()
      teardown?.()
    }
  }, [])
}

// Cursor crosshair: amber circle + four perpendicular ticks.
function makeCursorElement(): HTMLDivElement {
  const el = document.createElement('div')
  el.style.position = 'relative'
  el.style.width = '20px'
  el.style.height = '20px'
  el.style.pointerEvents = 'none'
  el.innerHTML = `
    <div style="
      position:absolute;
      top:50%;
      left:50%;
      width:10px;
      height:10px;
      transform:translate(-50%,-50%);
      border:2px solid #f59e0b;
      border-radius:50%;
      background:rgba(11,11,12,0.6);
      box-shadow:0 0 4px rgba(245,158,11,0.8);
    "></div>
    <div style="
      position:absolute;
      top:50%;
      left:0;
      width:6px;
      height:2px;
      transform:translateY(-50%);
      background:#f59e0b;
    "></div>
    <div style="
      position:absolute;
      top:50%;
      right:0;
      width:6px;
      height:2px;
      transform:translateY(-50%);
      background:#f59e0b;
    "></div>
    <div style="
      position:absolute;
      left:50%;
      top:0;
      width:2px;
      height:6px;
      transform:translateX(-50%);
      background:#f59e0b;
    "></div>
    <div style="
      position:absolute;
      left:50%;
      bottom:0;
      width:2px;
      height:6px;
      transform:translateX(-50%);
      background:#f59e0b;
    "></div>
  `
  return el
}

// Reference-origin marker: contrasting cyan diamond with a small
// "0" label inside, so the user sees at a glance where the relative
// origin sits in the model.
function makeReferenceElement(): HTMLDivElement {
  const el = document.createElement('div')
  el.style.position = 'relative'
  el.style.width = '24px'
  el.style.height = '24px'
  el.style.pointerEvents = 'none'
  el.innerHTML = `
    <div style="
      position:absolute;
      top:50%;
      left:50%;
      width:14px;
      height:14px;
      transform:translate(-50%,-50%) rotate(45deg);
      background:#0891b2;
      border:2px solid #67e8f9;
      box-shadow:0 0 6px rgba(34,211,238,0.7);
    "></div>
    <div style="
      position:absolute;
      top:50%;
      left:50%;
      transform:translate(-50%,-50%);
      font:600 9px ui-monospace, monospace;
      color:#0b0b0c;
      pointer-events:none;
    ">0</div>
    <div style="
      position:absolute;
      top:-14px;
      left:50%;
      transform:translateX(-50%);
      padding:1px 4px;
      font:600 9px ui-monospace, monospace;
      color:#67e8f9;
      background:rgba(11,11,12,0.85);
      border:1px solid rgba(34,211,238,0.5);
      border-radius:3px;
      white-space:nowrap;
    ">origem</div>
  `
  return el
}
